import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./ProjectList.css";
import { FaUser } from "react-icons/fa";
import EditUserDetailsModal from "./EditUserDetailsModal";

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [loggedUser, setLoggedUser] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTooltip]);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    fetch("https://loopsneller.com/api/user", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setLoggedUser(data));
  }, []);

  const handleUserUpdate = (updatedUser) => {
    setLoggedUser(updatedUser);
  };

  const user = JSON.parse(localStorage.getItem("user"));
  const role = user?.role || "User";

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch("https://loopsneller.com/api/projects", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch projects");
        }

        const data = await response.json();
        setProjects(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          {/* Left side - Projects heading */}
          <h2 className="mb-0">Projects</h2>

          {/* Right side - Tooltip and User Role */}
          <div className="d-flex align-items-center ms-auto">
            {/* Tooltip (User Profile Icon) */}
            <div className="position-relative ms-3" ref={tooltipRef}>
              <FaUser
                style={{ cursor: "pointer", fontSize: "1.5rem" }}
                onClick={() => setShowTooltip((prev) => !prev)}
              />

              {showTooltip && loggedUser && (
                <div
                  className="position-absolute bg-white shadow p-2 rounded"
                  style={{
                    top: "2rem",
                    left: "-5rem",
                    zIndex: 1000,
                    minWidth: "200px",
                  }}
                >
                  <div>
                    <strong>{loggedUser.name}</strong>
                  </div>
                  <div>{loggedUser.email}</div>
                  <button
                    className="btn btn-sm btn-outline-primary mt-2 w-100"
                    onClick={() => {
                      setShowEditModal(true);
                      setShowTooltip(false); // Optional: hide tooltip on click
                    }}
                  >
                    Edit Details
                  </button>
                </div>
              )}
            </div>

            {/* User Role */}
            <span className="badge bg-info text-dark px-3 py-2 rounded-pill fs-6 ms-3">
              Role: {role}
            </span>
          </div>
        </div>

        <div className="row">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="col-md-4 mb-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h5 className="placeholder-glow">
                    <span className="placeholder col-6"></span>
                  </h5>
                  <p className="placeholder-glow">
                    <span className="placeholder col-12"></span>
                    <span className="placeholder col-8"></span>
                  </p>
                  <button className="btn btn-primary btn-sm disabled placeholder col-5"></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error)
    return <p className="text-danger text-center mt-3">Error: {error}</p>;

  const displayedProjects = projects.slice(0, 6);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        {/* Left side - Projects heading */}
        <h2 className="mb-0">Projects</h2>

        {/* Right side - Tooltip and User Role */}
        <div className="d-flex align-items-center ms-auto">
          {/* Tooltip (User Profile Icon) */}
          <div className="position-relative ms-3" ref={tooltipRef}>
            <FaUser
              style={{ cursor: "pointer", fontSize: "1.5rem" }}
              onClick={() => setShowTooltip((prev) => !prev)}
            />

            {showTooltip && loggedUser && (
              <div
                className="position-absolute bg-white shadow p-2 rounded"
                style={{
                  top: "2rem",
                  left: "-5rem",
                  zIndex: 1000,
                  minWidth: "200px",
                }}
              >
                <div>
                  <strong>{loggedUser.name}</strong>
                </div>
                <div>{loggedUser.email}</div>
                <button
                  className="btn btn-sm btn-outline-primary mt-2 w-100"
                  onClick={() => {
                    setShowEditModal(true);
                    setShowTooltip(false); // Optional: hide tooltip on click
                  }}
                >
                  Edit Details
                </button>
              </div>
            )}
          </div>

          {/* User Role */}
          <span className="badge bg-info text-dark px-3 py-2 rounded-pill fs-6 ms-3">
            Role: {role}
          </span>
        </div>
      </div>

      <div className="row">
        {displayedProjects.length === 0 ? (
          <div className="col-12 text-center text-muted mt-4">
            <p>No projects added yet.</p>
          </div>
        ) : (
          displayedProjects.map((project) => (
            <div key={project.id} className="col-md-4 mb-3">
              <div className="card project-card shadow-sm">
                <div className="card-body">
                  <h5 className="card-title">{project.name}</h5>
                  <p className="card-text">
                    {project.description || "No description available."}
                  </p>
                  <a
                    href={`/projects/${project.id}`}
                    className="btn btn-primary btn-sm"
                  >
                    View Details
                  </a>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {projects.length > 9 && (
        <div className="text-center mt-3">
          <button
            className="btn btn-outline-primary"
            onClick={() => navigate("/projects")}
          >
            View More
          </button>
        </div>
      )}
      <EditUserDetailsModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        user={user}
        onUpdate={handleUserUpdate}
      />
    </div>
  );
};

export default ProjectList;
